import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { LISTINGS, CREATE_LISTING } from '../graphql';
import { Plus, Search, ShoppingBag, Tag, Phone, ImagePlus, X, Camera } from 'lucide-react';

const CATEGORIES = ['Books', 'Electronics', 'Furniture', 'Cycles', 'Stationery', 'Clothing', 'Other'];
const CONDITIONS = ['Like New', 'Good', 'Fair', 'For Parts'];
const MAX_PHOTOS = 3;

// Reads a file, downscales it on a canvas (max 900px long edge) and returns a JPEG data URL.
async function fileToDataUrl(file) {
  if (file.size > 8 * 1024 * 1024) {
    throw new Error(`"${file.name}" is larger than 8MB — please pick a smaller photo.`);
  }
  const rawUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Couldn't read "${file.name}". Please try again.`));
    reader.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`"${file.name}" doesn't look like a valid image.`));
    image.src = rawUrl;
  });
  const scale = Math.min(1, 900 / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.8);
}

function CoverPhoto({ images, title }) {
  const [idx, setIdx] = useState(0);
  if (!images || images.length === 0) return null;
  const i = idx % images.length;
  return (
    <button type="button" onClick={() => setIdx(v => (v + 1) % images.length)}
      title={images.length > 1 ? 'Click for next photo' : undefined}
      className="relative block w-full mb-3 focus:outline-none">
      <img src={images[i]} alt={title} className="h-36 w-full object-cover rounded-t-xl" />
      <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[11px] font-medium text-white bg-black/60 px-2 py-0.5 rounded-full">
        <Camera size={11} /> {i + 1}/{images.length}
      </span>
    </button>
  );
}

export default function MarketplacePage() {
  const [filters, setFilters] = useState({ category: '', search: '', maxPrice: '' });
  const { data, loading } = useQuery(LISTINGS, { variables: {
    category: filters.category || null,
    search: filters.search || null,
    maxPrice: filters.maxPrice ? parseFloat(filters.maxPrice) : null
  }});
  const [createListing, { loading: creating }] = useMutation(CREATE_LISTING, { refetchQueries: [LISTINGS] });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', price: '', category: 'Books', condition: 'Good', contactInfo: '' });
  const [contactShown, setContactShown] = useState(null);
  const [photos, setPhotos] = useState([]);          // compressed data URLs for the create form
  const [photoBusy, setPhotoBusy] = useState(false); // true while files are being read/compressed
  const [photoError, setPhotoError] = useState('');

  const handlePhotoFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file
    if (!files.length) return;
    setPhotoError('');
    const room = MAX_PHOTOS - photos.length;
    if (files.length > room) setPhotoError(`You can add up to ${MAX_PHOTOS} photos.`);
    setPhotoBusy(true);
    try {
      const urls = [];
      for (const f of files.slice(0, Math.max(room, 0))) urls.push(await fileToDataUrl(f));
      setPhotos(p => [...p, ...urls].slice(0, MAX_PHOTOS));
    } catch (err) {
      setPhotoError(err.message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    await createListing({ variables: { ...form, price: parseFloat(form.price), imageUrls: photos }});
    setForm({ title: '', description: '', price: '', category: 'Books', condition: 'Good', contactInfo: '' });
    setPhotos([]);
    setPhotoError('');
    setShowForm(false);
  };

  const listings = data?.listings || [];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Student Marketplace</h1>
          <p className="text-gray-500 text-sm mt-1">Buy & sell books, gadgets, hostel items</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowForm(!showForm)}>
          <Plus size={18} /> Sell Item
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input !pl-9" placeholder="Search items..."
            value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} />
        </div>
        <select className="input w-40" value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <input className="input w-36" type="number" placeholder="Max price ₹"
          value={filters.maxPrice} onChange={e => setFilters({...filters, maxPrice: e.target.value})} />
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="card mb-6 space-y-4">
          <h3 className="font-semibold">List an Item</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input className="input" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="NCERT Physics Class 12" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
              <input className="input" type="number" required value={form.price} onChange={e => setForm({...form, price: e.target.value})} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea className="input !h-20 resize-none" required value={form.description}
                onChange={e => setForm({...form, description: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select className="input" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
              <select className="input" value={form.condition} onChange={e => setForm({...form, condition: e.target.value})}>
                {CONDITIONS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Info</label>
              <input className="input" required value={form.contactInfo} onChange={e => setForm({...form, contactInfo: e.target.value})} placeholder="WhatsApp: 9876543210 or email" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Photos (up to {MAX_PHOTOS})</label>
              <div className="flex flex-wrap gap-2">
                {photos.map((src, i) => (
                  <div key={i} className="relative w-20 h-20">
                    <img src={src} alt={`Selected ${i + 1}`} className="w-20 h-20 object-cover rounded-xl border border-gray-100" />
                    <button type="button" title="Remove photo"
                      onClick={() => setPhotos(p => p.filter((_, j) => j !== i))}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-gray-800/80 text-white hover:bg-red-500 transition-colors">
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <label className={`w-20 h-20 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 transition-colors ${photoBusy ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:border-brand-400 hover:text-brand-500'}`}>
                    <ImagePlus size={20} />
                    <span className="text-[10px] font-medium">{photoBusy ? 'Adding…' : 'Add'}</span>
                    <input type="file" accept="image/*" multiple className="hidden" disabled={photoBusy} onChange={handlePhotoFiles} />
                  </label>
                )}
              </div>
              {photoError && <p className="text-xs text-red-500 mt-1">{photoError}</p>}
            </div>
          </div>
          <div className="flex gap-3">
            <button className="btn-primary" type="submit" disabled={creating || photoBusy}>
              {creating ? 'Listing…' : photoBusy ? 'Processing photos…' : 'List Item'}
            </button>
            <button className="btn-secondary" type="button" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map(item => (
            <div key={item.id} className="card hover:shadow-md transition-shadow">
              <CoverPhoto images={item.imageUrls} title={item.title} />
              <div className="flex items-start justify-between mb-2">
                <span className="badge bg-gray-100 text-gray-600">{item.category}</span>
                <span className={`badge ${item.condition === 'Like New' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {item.condition}
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
              <p className="text-sm text-gray-500 mb-3 line-clamp-2">{item.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-xl font-bold text-brand-600">₹{item.price.toFixed(0)}</span>
                <div className="text-right">
                  <p className="text-xs text-gray-400">{item.seller.name}</p>
                  {item.seller.college && <p className="text-xs text-gray-400">{item.seller.college}</p>}
                </div>
              </div>
              <button
                onClick={() => setContactShown(contactShown === item.id ? null : item.id)}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-xl transition-colors">
                <Phone size={15} />
                {contactShown === item.id ? item.contactInfo : 'Show Contact'}
              </button>
            </div>
          ))}

          {listings.length === 0 && (
            <div className="col-span-3 text-center py-16 text-gray-400">
              <ShoppingBag size={48} className="mx-auto mb-3 opacity-30" />
              <p>No listings yet. List an item to get started.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
